import {
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  sendUserAttributeVerificationCode,
  signIn as amplifySignIn,
  updatePassword,
} from 'aws-amplify/auth'

import { adminSignOut } from '../auth/adminSignOut'
import { withCognitoClientMetadataOptions } from './cognitoClientMetadata'

const authFlowType = () => process.env.REACT_APP_COGNITO_AUTH_FLOW || 'USER_SRP_AUTH'

const sessionWrapper = session => ({
  getIdToken: () => ({
    getJwtToken: () => session.tokens?.idToken?.toString() || '',
  }),
  getAccessToken: () => ({
    getJwtToken: () => session.tokens?.accessToken?.toString() || '',
  }),
})

const userWrapper = (user, attributes) => ({
  username: user.username,
  attributes,
  challengeName: undefined,
  challengeParam: undefined,
})

/** v4-shaped Auth surface kept for the call sites in admin and civic-champs-shared. */
export const Auth = {
  signIn: async (username, password) => {
    const { isSignedIn, nextStep } = await amplifySignIn({
      username,
      password,
      options: withCognitoClientMetadataOptions({ authFlowType: authFlowType() }),
    })

    if (!isSignedIn) {
      const step = nextStep.signInStep
      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD') {
        return {
          username,
          attributes: {},
          challengeName: 'NEW_PASSWORD_REQUIRED',
          challengeParam: nextStep.additionalInfo,
        }
      }
      if (step === 'CONFIRM_SIGN_UP') {
        const err = new Error('User is not confirmed.')
        err.code = 'UserNotConfirmedException'
        throw err
      }
      if (step === 'RESET_PASSWORD') {
        const err = new Error('Password reset required for the user.')
        err.code = 'PasswordResetRequiredException'
        throw err
      }
      const err = new Error(`Unsupported sign-in step: ${step}`)
      err.code = 'UnsupportedSignInStepException'
      throw err
    }

    const user = await getCurrentUser()
    const attributes = await fetchUserAttributes()
    return userWrapper(user, attributes)
  },

  signOut: () => adminSignOut(),

  currentSession: async () => {
    const session = await fetchAuthSession()
    if (!session.tokens?.idToken) {
      const err = new Error('No current user')
      err.code = 'NotAuthorizedException'
      throw err
    }
    return sessionWrapper(session)
  },

  currentAuthenticatedUser: async () => {
    const user = await getCurrentUser()
    let attributes
    try {
      attributes = await fetchUserAttributes()
    } catch (error) {
      const session = await fetchAuthSession()
      attributes = session.tokens?.idToken?.payload || {}
    }
    return userWrapper(user, attributes)
  },

  forgotPassword: username => resetPassword({ username, options: withCognitoClientMetadataOptions() }),

  forgotPasswordSubmit: (username, code, password) =>
    confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword: password,
      options: withCognitoClientMetadataOptions(),
    }),

  completeNewPassword: async (_user, newPassword) => {
    await confirmSignIn({
      challengeResponse: newPassword,
      options: withCognitoClientMetadataOptions(),
    })
    const current = await getCurrentUser()
    const attributes = await fetchUserAttributes()
    return userWrapper(current, attributes)
  },

  changePassword: async (_user, oldPassword, newPassword) => {
    await updatePassword({ oldPassword, newPassword })
  },

  verifyCurrentUserAttribute: attribute =>
    sendUserAttributeVerificationCode({
      userAttributeKey: attribute,
      options: withCognitoClientMetadataOptions(),
    }),

  verifyCurrentUserAttributeSubmit: (attribute, code) =>
    confirmUserAttribute({
      userAttributeKey: attribute,
      confirmationCode: code,
      options: withCognitoClientMetadataOptions(),
    }),
}
